/**
 * Supplier-related helpers: the supplier type taxonomy (one type per
 * supplier) and the search/sourcing-status logic used by the Inquiry
 * supplier selection flow.
 */

import { LOGISTICS_MODULE_ENABLED } from '../constants/featureFlags';

export const SUPPLIER_TYPES = ['Manufacturing', 'Product', 'Branding', 'Customization', 'Logistics', 'Fulfilment'];

// Legacy values -> best-fit entry in the current taxonomy.
const LEGACY_SUPPLIER_TYPE_MAP = {
  Standard: 'Product',
  BrandingOnly: 'Branding',
  DeliveryOnly: 'Logistics',
};

export const migrateLegacySupplierType = (supplier) => {
  if (SUPPLIER_TYPES.includes(supplier?.supplierType)) return supplier;
  const legacyType = Array.isArray(supplier?.supplierTypes) ? supplier.supplierTypes[0] : supplier?.supplierType;
  return { ...supplier, supplierType: LEGACY_SUPPLIER_TYPE_MAP[legacyType] || 'Product' };
};

// Logistics stays in SUPPLIER_TYPES itself (migrateLegacySupplierType still
// needs to recognize it on existing records) but is soft-removed from the
// options offered for new selections — see LOGISTICS_MODULE_ENABLED.
export const getSupplierTypeOptions = () => SUPPLIER_TYPES
  .filter((value) => LOGISTICS_MODULE_ENABLED || value !== 'Logistics')
  .map((value) => ({ value, label: value }));

// Which supplier types can fulfil a given line type — Service lines are
// branding/decoration work, Logistics lines are freight/handling. Returns
// null for 'Product' (or anything else), meaning "no restriction".
const LINE_TYPE_SUPPLIER_TYPES = {
  Service: ['Branding', 'Customization'],
  Logistics: ['Logistics'],
};

export const getSupplierTypesForLineType = (lineType) => LINE_TYPE_SUPPLIER_TYPES[lineType] || null;

// Supplier_Product field that evidences a supplier already quotes the given
// line type for at least one product — used as a forward-compatible
// eligibility check alongside the legacy manual supplierType, so a
// multi-role supplier (inquiry-module-redesign) doesn't need its single
// legacy type field to match before it can own a Service/Logistics
// catalogue item too.
const LINE_TYPE_SUPPLIER_PRODUCT_FIELD = {
  Service: 'avgBrandingPrice',
  Logistics: 'avgDeliveryPricePerItemPerKm',
};

/**
 * Whether `supplier` may own a catalogue item of the given line type
 * ('Service' | 'Logistics') — true if its legacy `supplierType` is in the
 * allowed set for that line type, OR it already has a Supplier_Product
 * record showing it quotes that role for some product (the same signal
 * `derivedSupplierTypes` used before the Services/Logistics catalogues
 * existed). Line types with no restriction (e.g. 'Product') always return true.
 */
export const isSupplierEligibleForLineType = (supplier, lineType, supplierProducts = []) => {
  const allowedLegacyTypes = getSupplierTypesForLineType(lineType);
  if (!allowedLegacyTypes) return true;
  if (allowedLegacyTypes.includes(supplier?.supplierType)) return true;
  const field = LINE_TYPE_SUPPLIER_PRODUCT_FIELD[lineType];
  if (!field) return false;
  return (supplierProducts || []).some((sp) => sp.supplierId === supplier?.id && sp[field] != null);
};

export const filterSuppliersByName = (suppliers = [], query = '') => {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return suppliers;
  return suppliers.filter((supplier) => (supplier.name || '').toLowerCase().includes(trimmed));
};

/**
 * Suppliers known to carry the given product (matched via Supplier_Product
 * records), each annotated with that product's price info. Falls back to
 * the full supplier list (no price info) when the item has no catalog
 * `productId` — e.g. a "New / Unknown" product picked on the Inquiry item.
 */
export const getSuppliersForProduct = (productId, suppliers = [], supplierProducts = []) => {
  if (!productId) return suppliers.map((supplier) => ({ ...supplier, priceInfo: null }));
  const byId = new Map(suppliers.map((supplier) => [supplier.id, supplier]));
  return supplierProducts
    .filter((entry) => entry.productId === productId && byId.has(entry.supplierId))
    .map((entry) => ({ ...byId.get(entry.supplierId), priceInfo: entry }));
};

export const computeSourcingStatus = (selectedSuppliers = []) => (
  selectedSuppliers.length > 0 ? 'Suppliers Selected' : 'Pending'
);

/** Certificate names held by a given supplier (from the Certificates collection). */
export const getCertificateNamesForSupplier = (supplierId, certificates = []) => (
  certificates.filter((cert) => cert.supplierId === supplierId).map((cert) => cert.name)
);

// ─────────────────────────────────────────────────────────────────────────
// Derived supplier types (inquiry-module-redesign) — a supplier's displayed
// type(s) are computed from its connected Supplier_Product catalog records
// rather than the manually-set `supplierType` field, so a supplier can
// simultaneously be Manufacturing, Services, and/or Logistics.
// ─────────────────────────────────────────────────────────────────────────
// Logistics soft-removed (LOGISTICS_MODULE_ENABLED) — no longer offered as a
// derived supplier type/badge/filter option, though a supplier's underlying
// Logistics catalogue rows (if any) are untouched.
export const DERIVED_SUPPLIER_TYPES = ['Manufacturing', 'Services', 'Logistics']
  .filter((type) => LOGISTICS_MODULE_ENABLED || type !== 'Logistics');

/**
 * Computes the set of role labels for a supplier — Manufacturing from its
 * Supplier_Product records (the same source the Supplier profile's Products
 * tab lists), Services/Logistics from actual ownership in the Products
 * Module's own Service/Logistics catalogues (the same source the profile's
 * Supplier Services/Supplier Logistics tabs list). Keeping this aligned with
 * what each tab actually displays avoids a supplier showing a role badge
 * with nothing behind it in the corresponding tab.
 * Returns an array of zero to three strings drawn from
 * DERIVED_SUPPLIER_TYPES, in that fixed order.
 *
 * @param {number} supplierId
 * @param {Array}  supplierProducts — the full initialSupplierProducts array
 * @param {Array}  serviceItems — the full initialServiceItems array
 * @param {Array}  logisticsItems — the full initialLogisticsItems array
 * @returns {string[]} e.g. ['Manufacturing', 'Services']
 */
export const derivedSupplierTypes = (supplierId, supplierProducts = [], serviceItems = [], logisticsItems = []) => {
  const records = (supplierProducts || []).filter((sp) => sp.supplierId === supplierId);
  const types = [];
  if (records.some((sp) => sp.avgItemPrice != null)) types.push('Manufacturing');
  if ((serviceItems || []).some((item) => item.supplierId === supplierId)) types.push('Services');
  if (LOGISTICS_MODULE_ENABLED && (logisticsItems || []).some((item) => item.supplierId === supplierId)) types.push('Logistics');
  return types;
};
